// =============================================================================
// Commercial page content + form options
// =============================================================================
// Centralized so the /commercial page, admin tab, and form share one source
// of truth. Status slugs MUST stay in sync with COMMERCIAL_INQUIRY_STATUSES
// in /app/backend/models.py.
// =============================================================================

import {
  Layers, Hammer, Wrench, Cloud, Hand, DoorOpen, Paintbrush, Star,
  Sofa, Droplets, Wind, Zap, Trees, Building2,
} from "lucide-react";

// -----------------------------------------------------------------------
// Positioning copy
// -----------------------------------------------------------------------
export const POSITIONING_COPY = `Black Ridge Construction brings a practical general contractor approach to commercial construction and facility updates. We can coordinate multi-trade commercial scopes as a full GC, or support single-division work when a client, owner, property manager, or larger contractor needs reliable execution in a specific area.

Our commercial work is built around clear communication, clean scheduling, field coordination, documentation, and practical problem-solving.`;

// -----------------------------------------------------------------------
// Relevant Commercial Experience
// -----------------------------------------------------------------------
export const COMMERCIAL_EXPERIENCE = {
  intro: `Black Ridge Construction has previous experience supporting imaging center remodels and facility updates, including specialized medical-space work where coordination, sequencing, cleanliness, and attention to detail matter.`,
  bullets: [
    "Imaging center remodel support",
    "MRI room updates",
    "Facility updates",
    "Selective demolition",
    "Shielding removal",
    "Framing adjustments",
    "Shielding installation coordination",
    "Drywall work",
    "Interior painting",
    "ACT / acoustical ceiling tile work",
    "Tile work",
    "Exterior updates",
    "Landscape-related exterior improvements",
  ],
  disclaimer:
    "Black Ridge Construction does not hold or claim specialty medical, engineering, or shielding certifications unless verified in writing. References to imaging/MRI work describe coordination and general construction support, not licensed medical specialty trades.",
};

// -----------------------------------------------------------------------
// Services by Division (accordion source)
// -----------------------------------------------------------------------
export const COMMERCIAL_DIVISIONS = [
  {
    code: "Div 01",
    label: "General Requirements / Project Coordination",
    icon: Layers,
    items: [
      "Site coordination",
      "Scope coordination",
      "Trade scheduling",
      "Photo documentation",
      "Punch-list management",
      "Owner communication",
      "Vendor and subcontractor coordination",
      "Facility update planning",
    ],
  },
  {
    code: "Div 02",
    label: "Existing Conditions / Demolition",
    icon: Hammer,
    items: [
      "Selective demolition",
      "Interior demo",
      "Removal of finishes",
      "Prep for remodels",
      "Facility update demolition",
      "Specialty removal coordination where applicable",
    ],
  },
  {
    code: "Div 06",
    label: "Wood, Plastics & Composites",
    icon: Hand,
    items: [
      "Framing adjustments",
      "Rough carpentry",
      "Blocking",
      "Backing",
      "Trim carpentry",
      "Custom build-outs",
      "Interior feature framing",
    ],
  },
  {
    code: "Div 07",
    label: "Thermal & Moisture Protection",
    icon: Cloud,
    items: [
      "Roofing coordination",
      "Roof repairs",
      "Roof replacement coordination",
      "Exterior waterproofing-related repairs",
      "Sealant and caulking",
      "Exterior envelope repairs",
      "Fascia, soffit, and trim repairs",
    ],
  },
  {
    code: "Div 08",
    label: "Openings",
    icon: DoorOpen,
    items: [
      "Door installation",
      "Window installation",
      "Hardware coordination",
      "Commercial door adjustments",
      "Interior doors",
      "Exterior doors",
      "Access-related repairs",
    ],
  },
  {
    code: "Div 09",
    label: "Finishes",
    icon: Paintbrush,
    items: [
      "Drywall",
      "Texture matching",
      "Interior painting",
      "Exterior painting",
      "Tile work",
      "Flooring coordination",
      "ACT / acoustical ceiling tile",
      "Wall finishes",
      "Finish repairs",
      "Make-ready work",
    ],
  },
  {
    code: "Div 10",
    label: "Specialties",
    icon: Star,
    items: [
      "Interior feature upgrades",
      "Specialty room finish coordination",
      "Wall protection coordination",
      "Facility accessory updates",
      "Signage coordination if needed",
    ],
  },
  {
    code: "Div 12",
    label: "Furnishings / Built-Ins",
    icon: Sofa,
    items: [
      "Built-ins",
      "Cabinetry coordination",
      "Breakroom updates",
      "Reception-area improvements",
      "Storage and shelving support",
    ],
  },
  {
    code: "Div 22",
    label: "Plumbing Coordination",
    icon: Droplets,
    items: [
      "Plumbing coordination through qualified licensed partners",
      "Fixture coordination",
      "Breakroom / bathroom update coordination",
    ],
    coordinationNote:
      "Black Ridge does not perform licensed plumbing work unless licensing is verified — licensed trades are coordinated through qualified partners.",
  },
  {
    code: "Div 23",
    label: "HVAC Coordination",
    icon: Wind,
    items: [
      "HVAC coordination through qualified licensed partners",
      "Equipment access coordination",
      "Facility update coordination",
    ],
    coordinationNote:
      "Black Ridge does not perform licensed HVAC work unless licensing is verified — licensed trades are coordinated through qualified partners.",
  },
  {
    code: "Div 26",
    label: "Electrical Coordination",
    icon: Zap,
    items: [
      "Electrical coordination through qualified licensed partners",
      "Lighting coordination",
      "Outlet or power needs coordination",
      "Specialty room electrical coordination",
    ],
    coordinationNote:
      "Black Ridge does not perform licensed electrical work unless licensing is verified — licensed trades are coordinated through qualified partners.",
  },
  {
    code: "Div 32",
    label: "Exterior Improvements",
    icon: Trees,
    items: [
      "Landscape-related exterior updates",
      "Exterior repairs",
      "Fencing",
      "Drainage-related repairs",
      "Walkway / patio repairs",
      "Exterior refresh work",
      "Site appearance improvements",
    ],
  },
  {
    code: "Care",
    label: "Commercial Maintenance / Property Care",
    icon: Building2,
    items: [
      "Commercial maintenance memberships",
      "Recurring site visits",
      "RoofWatch Commercial",
      "PropertyCare Core",
      "PropertyCare Pro",
      "Portfolio Reserve",
      "Photo reports",
      "Leak triage",
      "Make-ready support",
      "Punch-list repairs",
      "Faster quoting and repair coordination",
    ],
  },
];

// -----------------------------------------------------------------------
// Commercial Service Categories (top-level cards on the page)
// -----------------------------------------------------------------------
export const COMMERCIAL_SERVICE_CARDS = [
  {
    title: "Commercial Remodels",
    body: "Interior remodels, facility updates, finish upgrades, drywall, paint, ACT, tile, flooring, and multi-trade coordination.",
  },
  {
    title: "Facility Updates",
    body: "Practical improvements for offices, medical spaces, retail sites, and managed properties.",
  },
  {
    title: "Imaging Center / MRI Room Support",
    body: "Experience supporting imaging center remodels, MRI room updates, shielding-related coordination, demolition, framing adjustments, drywall, paint, ACT, and tile work.",
  },
  {
    title: "Commercial Roofing",
    body: "Roof inspections, roof repairs, replacement coordination, storm follow-up, and RoofWatch Commercial support.",
  },
  {
    title: "Commercial Maintenance",
    body: "Recurring maintenance memberships, photo documentation, leak triage, punch-list work, and repair coordination.",
  },
  {
    title: "Exterior & Site Improvements",
    body: "Exterior repairs, landscape-related updates, trim, fascia, soffit, drainage-related repairs, fencing, and site appearance improvements.",
  },
  {
    title: "Single-Division Support",
    body: "Black Ridge can support specific divisions such as demo, framing, drywall, paint, tile, ACT, roofing, exterior repairs, or punch-list work.",
  },
  {
    title: "Full Commercial GC Coordination",
    body: "Black Ridge can coordinate multi-trade scopes, subcontractors, scheduling, documentation, and project closeout as a commercial GC where appropriate.",
  },
];

// -----------------------------------------------------------------------
// FAQs
// -----------------------------------------------------------------------
export const COMMERCIAL_FAQS = [
  {
    q: "Does Black Ridge Construction handle commercial projects?",
    a: "Yes. Black Ridge Construction supports commercial remodels, facility updates, roofing, exterior repairs, maintenance, and division-based construction services in Austin and Central Texas.",
  },
  {
    q: "Can Black Ridge act as a commercial GC?",
    a: "Black Ridge can coordinate multi-trade commercial scopes where appropriate, including scheduling, subcontractor coordination, documentation, and project communication.",
  },
  {
    q: "Can you handle single-division commercial work?",
    a: "Yes. Black Ridge can support single divisions such as demo, framing, drywall, paint, tile, ACT, roofing, exterior repairs, and punch-list work when needed.",
  },
  {
    q: "Do you have medical or imaging center experience?",
    a: "Black Ridge has previous experience supporting imaging center remodels and MRI room facility updates, including demo, shielding-related coordination, framing adjustments, drywall, paint, ACT, tile, exterior updates, and landscape-related improvements.",
  },
  {
    q: "Do you self-perform licensed electrical, plumbing, or HVAC work?",
    a: "Licensed trade work may be coordinated through qualified licensed partners when required. Black Ridge should not be represented as self-performing licensed work unless licensing is verified.",
  },
  {
    q: "Do you offer commercial maintenance plans?",
    a: "Yes. Black Ridge offers PropertyCare and RoofWatch Commercial options for recurring site visits, photo documentation, leak triage, repair coordination, and faster quoting.",
  },
  {
    q: "Can you work with property managers or commercial portfolios?",
    a: "Yes. Black Ridge can support property managers, facility managers, investors, multifamily owners, and commercial portfolio clients.",
  },
];

// -----------------------------------------------------------------------
// Form options
// -----------------------------------------------------------------------
export const PROPERTY_TYPES = [
  "Office",
  "Retail",
  "Medical / imaging center",
  "Restaurant / hospitality",
  "Light industrial",
  "Multifamily",
  "Managed property",
  "Commercial portfolio",
  "Other",
];

export const SERVICE_NEEDED_OPTIONS = [
  "Commercial remodel",
  "Facility update",
  "Roofing",
  "Maintenance",
  "Exterior improvements",
  "Single-division work",
  "MRI / imaging room support",
  "Punch-list / make-ready",
  "Not sure yet",
];

export const PROJECT_TYPE_OPTIONS = [
  "Full GC coordination",
  "Single division",
  "Maintenance / recurring",
  "Emergency repair",
  "Budgeting / early planning",
];

export const OCCUPIED_OPTIONS = ["Occupied", "Vacant", "Partial / phased", "Unsure"];

export const GC_OR_DIVISION_OPTIONS = [
  "Full GC coordination",
  "Single division",
  "Either / open to recommendation",
];

// -----------------------------------------------------------------------
// Admin status workflow
// -----------------------------------------------------------------------
export const COMMERCIAL_INQUIRY_STATUSES = [
  { slug: "new", label: "New", tone: "slate" },
  { slug: "reviewing", label: "Reviewing", tone: "amber" },
  { slug: "contacted", label: "Contacted", tone: "sky" },
  { slug: "site-walk-scheduled", label: "Site Walk Scheduled", tone: "violet" },
  { slug: "quoted", label: "Quoted", tone: "indigo" },
  { slug: "won", label: "Won", tone: "emerald" },
  { slug: "lost", label: "Lost", tone: "red" },
  { slug: "archived", label: "Archived", tone: "gray" },
];
